/**
 * PasswordField — autenticazione Phase 5 (IMPL-P5-008).
 *
 * Wraps shadcn `Input type="password"` with a show/hide toggle. The component
 * is a forwarded-ref input so it composes cleanly with `react-hook-form`'s
 * `register('password')`.
 *
 * Accessibility:
 *  - Toggle button has an explicit `aria-label` that flips with state.
 *  - `aria-pressed` reflects current visibility for SR users.
 *  - `aria-describedby` (passed through from the parent) chains to error
 *    nodes so announcements include validation copy.
 */
import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';

type PasswordFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  invalid?: boolean;
};

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ className, invalid, ...props }, ref) => {
    const [show, setShow] = useState(false);
    const baseInputClasses =
      'flex h-9 w-full rounded-md border border-input bg-transparent pl-3 pr-10 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm';

    return (
      <div className="relative">
        <input
          ref={ref}
          type={show ? 'text' : 'password'}
          aria-invalid={invalid || undefined}
          className={cn(baseInputClasses, invalid && 'border-destructive', className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setShow((prev) => !prev)}
          aria-label={show ? 'Nascondi password' : 'Mostra password'}
          aria-pressed={show}
          tabIndex={0}
          className="absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {show ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
        </button>
      </div>
    );
  },
);

PasswordField.displayName = 'PasswordField';
